/**
 * The AgendaGPT submission — one implementation, two callers.
 *
 * Extracted from `routes/client-engagement/AgendaForm.tsx` on 2026-08-13 when
 * the Search Detail page grew an AgendaGPT button. Every property below was
 * paid for by a debugging session against a workflow that answered 200 while
 * dropping the submission, so the two callers must not each own a copy.
 *
 * Why this POSTs straight from the browser rather than through our API: n8n
 * Cloud's Form Trigger blocks cross-origin iframes (`X-Frame-Options` AND
 * `frame-ancestors 'self'`, with the bypass Enterprise-only), but leaves CORS
 * wide open for fetch POSTs — it reflects the request Origin. So the SPA posts
 * directly. There is no nu-assist endpoint in this path at all.
 *
 * Three things that look like details and are not:
 *
 * 1. `multipart/form-data` is the ONLY content type that works. Both
 *    `application/json` and `application/x-www-form-urlencoded` answer
 *    `500 "Workflow could not be started"` (confirmed via curl, 2026-06-12).
 * 2. No explicit `Content-Type` header. The browser writes it from the
 *    `FormData` body, boundary included; setting one by hand replaces the
 *    generated boundary and the request stops parsing.
 * 3. The field names are n8n's generated positional input names — `field-0` is
 *    the first configured field (Search ID), `field-1` the second (Your Email).
 *    NOT the UI labels, NOT snake_case. n8n returns `200 {"status":200}` for
 *    ANY field names, so a rename is invisible at the call site and surfaces as
 *    nulls inside the workflow. If the fields are reordered in the n8n editor
 *    these indices shift and submissions silently arrive null again.
 */

/**
 * Production AgendaGPT Form Trigger URL. The single hosted form serves both dev
 * and prod consumers (n8n's CORS reflects the request Origin). Devs iterating
 * against an unpublished workflow override via `VITE_AGENDA_FORM_URL` to point
 * at `/form-test/<uuid>`.
 */
const DEFAULT_FORM_URL =
  "https://nuse.app.n8n.cloud/form/aeb0f7c7-0a44-4460-9aee-96af1f6660f7";

export const AGENDA_FORM_URL = import.meta.env.VITE_AGENDA_FORM_URL ?? DEFAULT_FORM_URL;

/** n8n's positional input names. Exported so the form page's DOM `name`
 *  attributes and this module's FormData keys cannot drift apart. */
export const SEARCH_ID_FIELD = "field-0";
export const EMAIL_FIELD = "field-1";

export interface AgendaSubmissionInput {
  searchId: string;
  email: string;
}

/** An HTTP failure carrying the status, so callers can split 5xx from 4xx. */
export type AgendaSubmissionError = Error & { status?: number };

/**
 * POST the AgendaGPT form to the n8n Form Trigger.
 *
 * Throws on non-2xx so a `useMutation` `onError` handler runs. The message
 * prefers the response body's `message` field when there is one, and falls back
 * to the HTTP status.
 */
export async function submitAgendaForm(input: AgendaSubmissionInput): Promise<void> {
  const fd = new FormData();
  fd.append(SEARCH_ID_FIELD, input.searchId);
  fd.append(EMAIL_FIELD, input.email);

  const res = await fetch(AGENDA_FORM_URL, {
    method: "POST",
    body: fd,
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: string };
      if (typeof body.message === "string" && body.message.length > 0) {
        message = body.message;
      }
    } catch {
      // Body wasn't JSON — keep the HTTP status as the message.
    }
    // 5xx + network failures both surface here. The caller's onError chooses
    // the user-facing wording based on the status range.
    const err = new Error(message) as AgendaSubmissionError;
    err.status = res.status;
    throw err;
  }
}

/**
 * The toast copy for a failed submission.
 *
 * Shared for the same reason the POST is: the split is a judgement — a 5xx or a
 * network failure means "we could not reach it", a 4xx means the server
 * understood and refused, and only the second is worth quoting back — and two
 * copies of a judgement drift.
 */
export function agendaErrorMessage(error: AgendaSubmissionError): string {
  const status = error.status ?? 0;
  if (status === 0 || status >= 500) return "Could not reach the server";
  return `Could not submit: ${error.message}`;
}
